import { type Query, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import type { Day } from "../../api/types";
import { useSession } from "../../app/session-context";
import { type Draft, DraftStore, DraftsContext } from "./drafts-context";

/**
 * Every cached day with the block gets its saved draft: the form starts from
 * the server's once nothing typed is waiting (`DraftStore.start`), and a day
 * shown again from the cache (another day and back, Change client) must not
 * hold what was there before this save.
 *
 * A fetch of such a day already on its way (its poll, or coming back to it)
 * may have been answered before this save, and would land after it with the
 * older draft: the form would then start from that, as if the typing were
 * gone. So it's cancelled first, and the day fetched again once the cache
 * has the save.
 */
async function keepCachedDays(queryClient: QueryClient, blockId: string, draft: Draft): Promise<void> {
  const withBlock = (query: Query): boolean =>
    (query.state.data as Day | undefined)?.blocks.some((block) => block.id === blockId) ?? false;
  const days = { queryKey: ["day"], predicate: withBlock };
  const fetching = queryClient.isFetching(days) > 0;
  if (fetching) {
    await queryClient.cancelQueries(days);
  }
  queryClient.setQueriesData<Day>({ queryKey: ["day"] }, (day) =>
    day?.blocks.some((block) => block.id === blockId)
      ? { ...day, blocks: day.blocks.map((block) => (block.id === blockId ? { ...block, draft } : block)) }
      : day,
  );
  if (fetching) {
    void queryClient.invalidateQueries(days);
  }
}

/** The session's Log to Autotask drafts, saved on their blocks (#218; ADR 0097). */
export function DraftsProvider({ children }: { children: ReactNode }) {
  // One client for the page's life (Bootstrap makes it once, for its sign-in).
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [store] = useState(
    () =>
      new DraftStore(async (blockId, draft) => {
        const saved = await api.saveBlockDraft(blockId, draft);
        await keepCachedDays(queryClient, blockId, { title: saved.title, notes: saved.notes });
        return saved;
      }),
  );
  return <DraftsContext.Provider value={store}>{children}</DraftsContext.Provider>;
}
